import React, {useState} from "react";
import {useMessages} from "i18n";
import type {SponsorTakeover} from "../takeoverModel";
import {PosterCanvas} from "./PosterCanvas";
import {
	PosterAccentLine,
	PosterAdLabel,
	PosterCta,
	PosterHeadline,
	PosterSubline,
	type PosterLabelAnchor,
} from "./SponsorPoster.styles";
import {AD_LABEL_TESTID, POSTER_TESTID} from "./takeoverTestIds";

/**
 * The poster a WALLPAPER booking gets in place of the ad chip (kitecorp/transferify-ui#1866).
 *
 * The chip is a panel of ours that happens to carry an advertiser's words. A wallpaper booking buys
 * the page, so this is the opposite arrangement: the copy is typeset on the photograph itself, in the
 * placement and the colours the advertiser chose, with their mark in the corner opposite the download
 * card and one pill to click. `download/pageBackdrop.ts`'s `sponsorDisclosure` decides which of the
 * two a page mounts, off the same decision that chose the art — so a chip can never appear beside a
 * full-page wallpaper and a poster can never be laid over a card-side backdrop.
 *
 * WHAT DOES NOT CHANGE between the two: the disclosure label is rendered unconditionally and FIRST,
 * before any sponsor-authored string, under the chip's own test id and carrying the chip's own catalog
 * string. An advertisement is labelled as one whatever the copy says and whatever stage it is on,
 * which is the 2026-08-06 legal guardrail — and this stage, which puts art over the whole page, is
 * where losing the label would be easiest to miss. The link keeps `rel="nofollow sponsored noopener
 * noreferrer"` for the same reasons it does on the chip.
 *
 * WHERE THE LABEL IS since kitecorp/transferify-ui#2112: the bottom-right corner of the screen, not the
 * top of the advertiser's block. The owner reviewed six placements and chose that one ("i don't think
 * we need to add word 'publicitate' above our text"), so the block is now the advertiser's words alone
 * and the disclosure sits in the page's own corner. Every clause of the paragraph above survived the
 * move intact; `PosterAdLabel` and `PosterCanvas`'s label slot are what keep it that way.
 *
 * Renders `null` when there is no takeover, so absence costs no element and no layout.
 */

interface PosterProps {
	takeover: SponsorTakeover | null;
	/**
	 * The element the wallpaper's photograph fills — the download page's own `ShowcaseArt`, which on
	 * this stage is `inset: 0` of the page.
	 *
	 * Passed in rather than found, because the contrast scrim has to sample the pixels under the type
	 * and the poster's own region is NOT that box: it composes in the space beside the download card,
	 * so mapping a sample against it would read the wrong fraction of the picture and would scale the
	 * `cover` crop off the wrong dimensions. `null` until the page has mounted it, which the sampling
	 * hook treats as "not measured yet" and therefore scrims.
	 */
	artLayer: HTMLElement | null;
	/**
	 * Which corner the disclosure is pinned to: the browser viewport on the recipient's own page, the
	 * enclosing stage in a bounded picture of it. See {@link PosterLabelAnchor} for why neither can be
	 * the default.
	 */
	labelAnchor: PosterLabelAnchor;
	/**
	 * Whether the block is laid over the picture at EVERY width, rather than only beside the download
	 * card at the showcase breakpoint. Forwarded to {@link PosterCanvas.composed}, which states the
	 * rule; `false` is the recipient page's own behaviour and stays the default.
	 *
	 * A bounded stage that is a picture of a PHONE has to pass `true` (kitecorp/transferify-ui#2143).
	 * The recipient page's rule is a viewport media query, and a preview stage's own width is not the
	 * viewport: inside a booking form open on a desktop, a 390px stage would take the wide branch and
	 * squeeze the advertiser's copy into the 24px left over beside a phone-width card. Measured before
	 * this prop existed: the headline set one letter per line.
	 */
	composed?: boolean;
}

export function SponsorPoster(
	{takeover, artLayer, labelAnchor, composed = false}: PosterProps,
): React.ReactElement | null {
	// Before any hook, exactly as the art layer does: a page with no sponsor mounts nothing, so it
	// starts no observers and decodes no pictures either.
	return takeover === null
		? null
		: <MountedPoster
			takeover={takeover}
			artLayer={artLayer}
			labelAnchor={labelAnchor}
			composed={composed}
		/>;
}

/** The mounted poster. Split out purely so the hooks below run only when there is one — see above. */
function MountedPoster(
	{takeover, artLayer, labelAnchor, composed}: {
		takeover: SponsorTakeover;
		artLayer: HTMLElement | null;
		labelAnchor: PosterLabelAnchor;
		composed: boolean;
	},
): React.ReactElement {
	const m = useMessages().sponsorTakeover;
	/*
	 * A brand mark that FAILED TO LOAD renders its alt text — visible copy, in the reader's default
	 * colour, over somebody else's photograph, with no contrast guarantee at all. That is the one shape
	 * "an absent logo renders with no gap" was meant to exclude, so a broken load is treated exactly
	 * like an absent one and the element leaves the DOM.
	 */
	const [logoBroken, setLogoBroken] = useState(false);

	return (
		<PosterCanvas
			options={takeover.options}
			imageUrls={takeover.imageUrls}
			artLayer={artLayer}
			testId={POSTER_TESTID}
			ariaLabel={m.regionAria(takeover.sponsorName)}
			logo={takeover.logoUrl !== null && !logoBroken
				? {url: takeover.logoUrl, alt: m.logoAlt(takeover.sponsorName)}
				: null}
			onLogoError={() => setLogoBroken(true)}
			composed={composed}
			label={
				<PosterAdLabel data-testid={AD_LABEL_TESTID} $anchor={labelAnchor}>{m.label}</PosterAdLabel>
			}
		>
			<PosterHeadline>{takeover.headline}</PosterHeadline>
			{/*
			  * The SECOND display line — the tagline, set at the headline's size rather than as a caption
			  * under it. That is the owner's reference: two lines of one statement. Both are painted in
			  * the chosen text colour since kitecorp/transferify-ui#2416. Optional, and absent means
			  * absent: the model already normalises a blank tagline to null so nothing reserves a line
			  * for it.
			  */}
			{takeover.tagline !== null && <PosterAccentLine>{takeover.tagline}</PosterAccentLine>}
			{takeover.options?.subline && <PosterSubline>{takeover.options.subline}</PosterSubline>}
			<PosterCta
				href={takeover.ctaUrl}
				target="_blank"
				rel="nofollow sponsored noopener noreferrer"
			>
				{takeover.ctaLabel}
			</PosterCta>
		</PosterCanvas>
	);
}
